import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import { ButtonDotsLoader } from 'components/Button/ButtonDotsLoader'
import { ChooseActionsLabel } from 'components/ChooseActionsLabel/ChooseActionsLabel'
import { useServiceModeSwitchWaiting } from 'hooks/useServiceModeSwitchWaiting'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'
import { useSelector } from 'react-redux'
import { selectRobotActiveMode, selectRobotTelemetry } from 'store/selectors/robot'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import { ROBOT_MODES } from 'utils/constants'
import type { RootState } from 'utils/interfaces'
import { ActionType, type ILotModelConfig } from 'utils/servicePointsCatalog'

import { ServiceCalibPointIcon } from './ServiceCalibPointIcon'
import { ServicePointActionChips } from './ServicePointActionChips'
import { catalogCrossTabLog } from './catalogCrossTabDebug'
import {
  actionTypesFromRosCodes,
  getCatalogItemAllowedActions,
  needsCatalogActionSelection,
  resolveSelectedCatalogActions,
} from './catalogItemActions'
import { resolveCatalogPrimaryAction } from './catalogPrimaryAction'
import { useCatalogOperationTimeoutActionKey } from './catalogOperationTimeoutNotice'
import {
  CATALOG_SERVICE_MODE,
  buildCatalogServiceMetadata,
  findCatalogItemByServiceMetadata,
  getCatalogPointActionTypeFromItem,
  getCatalogServiceActionLabel,
  isCatalogServiceRowActive,
  isCatalogServiceRowActivating,
  isCatalogServiceModeAvailableOnRobot,
  supportsCatalogServiceAction,
} from './serviceCalibLotService'
import type { ServiceCalibCatalogData, ServiceCalibCatalogItem } from './serviceCalibTypes'
import {
  getEffectiveServicePointCalibrationRows,
  getResetCalibButtonTitle,
  isRobotLidarLocalizedForServicePointCalib,
  isServiceCalibItemCalibrated,
} from './serviceCalibUtils'
import { serviceModeActionKey } from './serviceModeActionKeys'

import serviceStyle from './Service.module.css'
import style from './ServiceCalibCatalog.module.css'

export type ServiceCalibCatalogProps = {
  robotId: number
  isOnline: boolean
  catalog: ServiceCalibCatalogData | null
  loading: boolean
  error: string | null
  selectedKey: string | null
  onSelectKey: (key: string) => void
}

const MAINTENANCE_GROUP_KEY = 'maintenance-points'
/** Show MP rows without a collapsible group when count is at most this. */
const MAINTENANCE_FLAT_MAX = 3

const ServiceCalibCatalog = ({
  robotId,
  isOnline,
  catalog,
  loading,
  error,
  selectedKey,
  onSelectKey,
}: ServiceCalibCatalogProps) => {
  const {
    isGlobalLocked,
    isActionLoading,
    confirmedCalibKey: confirmedActiveCalibKey,
    confirmedServiceKey: confirmedActiveServiceKey,
    recentCalibFinishSuccessKey,
    calibFinishOnboardReadyKey,
  } = useServiceModeSwitchWaiting(catalog)
  const commandsLockedEffective = isGlobalLocked
  const timedOutActionKey = useCatalogOperationTimeoutActionKey(robotId)
  const telemetry = useSelector(selectRobotTelemetry)
  const activeMode = useSelector(selectRobotActiveMode)
  const activeModeMetadata = useSelector((state: RootState) => state.robot.robotInfo?.activeModeMetadata)
  const robotType = useSelector((state: RootState) => state.robot.robotInfo?.robot_type)
  const calibrationRows = getEffectiveServicePointCalibrationRows(telemetry, {
    activeMode,
    activeModeMetadata,
  })
  const onboardServicePoints = telemetry?.onboardServicePoints
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [lotModels, setLotModels] = useState<ILotModelConfig[]>([])
  const [selectedActionsByKey, setSelectedActionsByKey] = useState<Record<string, ActionType[]>>({})

  useEffect(() => {
    MainApi.fetchLotModels()
      .then(models => setLotModels(models as ILotModelConfig[]))
      .catch(console.error)
  }, [])

  // Hydrate chips from API/WS active_mode_metadata.action_type[] (outgoing command merge on
  // backend). Covers multi-browser and reload while mode 17 is active.
  useEffect(() => {
    if (!catalog || activeMode !== ROBOT_MODES.MODE_SERVICE_POINT || !activeModeMetadata?.action_type?.length) {
      return
    }
    const item = findCatalogItemByServiceMetadata(catalog, activeModeMetadata as Record<string, unknown>)
    if (!item) {
      return
    }
    const actions = actionTypesFromRosCodes(activeModeMetadata.action_type)
    if (!actions.length) {
      return
    }
    setSelectedActionsByKey(prev => {
      const current = prev[item.key] ?? []
      if (current.length === actions.length && current.every((value, index) => value === actions[index])) {
        return prev
      }
      return { ...prev, [item.key]: actions }
    })
  }, [activeMode, activeModeMetadata, catalog])
  const [calibFinishFailedKeys, setCalibFinishFailedKeys] = useState<Set<string>>(() => new Set())

  const activeCalibFinishLoading = confirmedActiveCalibKey
    ? isActionLoading(serviceModeActionKey.catalogCalibFinish(confirmedActiveCalibKey))
    : false

  /** Robot holds mode 15 — block other catalog rows. */
  const calibSessionBlocksOtherRows = Boolean(confirmedActiveCalibKey) || activeCalibFinishLoading

  const prevRobotIdRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    prevRobotIdRef.current = robotId
    setCalibFinishFailedKeys(new Set())
  }, [robotId])

  const lidarLocalizedForCalib = isRobotLidarLocalizedForServicePointCalib(telemetry?.localization_info)
  const isCatalogServiceModeAvailable = isCatalogServiceModeAvailableOnRobot(onboardServicePoints)

  const modeInProgress =
    activeMode !== ROBOT_MODES.MODE_NOT_SET &&
    activeMode !== ROBOT_MODES.MODE_MANUAL &&
    activeMode !== ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT &&
    activeMode !== ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT &&
    activeMode !== ROBOT_MODES.MODE_SERVICE_POINT

  const prevCalibFinishLoadingRef = useRef<Record<string, boolean>>({})

  useEffect(() => {
    if (!catalog) {
      return
    }
    const items = catalog.stationGroups.flatMap(g => g.lots).concat(catalog.maintenancePoints)
    const failedKeys: string[] = []
    for (const item of items) {
      const finishKey = serviceModeActionKey.catalogCalibFinish(item.key)
      const finishLoading = isActionLoading(finishKey)
      const wasLoading = prevCalibFinishLoadingRef.current[item.key]
      if (wasLoading && !finishLoading && !isServiceCalibItemCalibrated(item, calibrationRows)) {
        failedKeys.push(item.key)
      }
      prevCalibFinishLoadingRef.current[item.key] = finishLoading
    }
    if (failedKeys.length === 0) {
      return
    }
    setCalibFinishFailedKeys(prev => {
      const next = new Set(prev)
      failedKeys.forEach(key => next.add(key))
      return next
    })
  }, [calibrationRows, catalog, isActionLoading])

  useEffect(() => {
    if (!catalog || calibFinishFailedKeys.size === 0) {
      return
    }
    const items = catalog.stationGroups.flatMap(g => g.lots).concat(catalog.maintenancePoints)
    const cleared = items.filter(
      item => calibFinishFailedKeys.has(item.key) && isServiceCalibItemCalibrated(item, calibrationRows)
    )
    if (cleared.length === 0) {
      return
    }
    setCalibFinishFailedKeys(prev => {
      const next = new Set(prev)
      cleared.forEach(item => next.delete(item.key))
      return next
    })
  }, [calibFinishFailedKeys, calibrationRows, catalog])

  const isGroupExpanded = useCallback((groupKey: string) => expandedGroups[groupKey] ?? false, [expandedGroups])

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !(prev[groupKey] ?? false),
    }))
  }, [])

  const toggleCatalogAction = useCallback((itemKey: string, action: ActionType) => {
    setSelectedActionsByKey(prev => {
      const current = prev[itemKey] ?? []
      const next = current.includes(action) ? current.filter(value => value !== action) : [...current, action]
      return { ...prev, [itemKey]: next }
    })
  }, [])

  const dispatchCalib = (item: ServiceCalibCatalogItem, mode: ROBOT_MODES) => {
    const actionKey =
      mode === ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT
        ? serviceModeActionKey.catalogReset(item.key)
        : serviceModeActionKey.catalogCalib(item.key)
    const awaitCalib =
      mode === ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT
        ? {
            target: item.target,
            stationHardwareId: item.stationHardwareId,
            lotId: item.lotId,
            maintenancePointId: item.maintenancePointId,
            expectedReady: false,
          }
        : undefined
    if (mode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT) {
      setCalibFinishFailedKeys(prev => {
        if (!prev.has(item.key)) {
          return prev
        }
        const next = new Set(prev)
        next.delete(item.key)
        return next
      })
    }
    store.dispatch(
      robotThunks.setRobotMode(robotId, mode, item.metadata, {
        actionKey,
        catalogPointActionType: getCatalogPointActionTypeFromItem(item),
        awaitCalib,
      })
    )
  }

  const dispatchCalibFinish = (item: ServiceCalibCatalogItem) => {
    setCalibFinishFailedKeys(prev => {
      if (!prev.has(item.key)) {
        return prev
      }
      const next = new Set(prev)
      next.delete(item.key)
      return next
    })
    store.dispatch(
      robotThunks.setRobotNotSetMode(robotId, {
        actionKey: serviceModeActionKey.catalogCalibFinish(item.key),
        awaitCalib: {
          target: item.target,
          stationHardwareId: item.stationHardwareId,
          lotId: item.lotId,
          maintenancePointId: item.maintenancePointId,
          expectedReady: true,
        },
      })
    )
  }

  const dispatchCatalogService = (item: ServiceCalibCatalogItem) => {
    if (!supportsCatalogServiceAction(item)) {
      return
    }

    const activeOnRow = isCatalogServiceRowActive(item, confirmedActiveServiceKey)
    if (activeOnRow) {
      const serviceKey = item.key
      catalogCrossTabLog('stop-click', { robotId, serviceKey })
      void store.dispatch(
        robotThunks.setRobotNotSetMode(robotId, {
          actionKey: serviceModeActionKey.catalogServiceStop(item.key),
        })
      )
      return
    }

    const metadata = buildCatalogServiceMetadata(item, lotModels, selectedActionsByKey, robotType)
    if (!metadata) {
      return
    }
    const selectedActions = resolveSelectedCatalogActions(item, selectedActionsByKey, lotModels, robotType)
    store.dispatch(
      robotThunks.setRobotMode(robotId, CATALOG_SERVICE_MODE, metadata, {
        actionKey: serviceModeActionKey.catalogService(item.key),
        catalogPointActionType: getCatalogPointActionTypeFromItem(item, selectedActions),
      })
    )
  }

  const renderPrimaryActionButton = (
    item: ServiceCalibCatalogItem,
    rowState: {
      calibrated: boolean
      showFinishCalib: boolean
      calibFailed: boolean
      needsActionSelection: boolean
      calibLoading: boolean
      calibFinishLoading: boolean
      serviceStartLoading: boolean
      serviceStopLoading: boolean
      serviceRowActivating: boolean
      blockedByOtherCatalogOperation: boolean
    }
  ) => {
    const primaryAction = resolveCatalogPrimaryAction({
      item,
      calibrated: rowState.calibrated,
      showFinishCalib: rowState.showFinishCalib,
      calibFailed: rowState.calibFailed,
      needsActionSelection: rowState.needsActionSelection,
      lotModels,
      selectedActionsByKey,
      robotType,
      isOnline,
      lidarLocalizedForCalib,
      modeInProgress,
      blockedByOtherCatalogOperation: rowState.blockedByOtherCatalogOperation,
      commandsLockedEffective,
      isCatalogServiceModeAvailable,
      serviceUiActive: confirmedActiveServiceKey != null,
      confirmedServiceKey: confirmedActiveServiceKey,
      calibLoading: rowState.calibLoading,
      calibFinishLoading: rowState.calibFinishLoading,
      serviceStartLoading: rowState.serviceStartLoading,
      serviceStopLoading: rowState.serviceStopLoading,
      serviceRowActivating: rowState.serviceRowActivating,
      noConfirmation:
        timedOutActionKey === serviceModeActionKey.catalogService(item.key) ||
        timedOutActionKey === serviceModeActionKey.catalogServiceStop(item.key) ||
        timedOutActionKey === serviceModeActionKey.catalogCalib(item.key) ||
        timedOutActionKey === serviceModeActionKey.catalogCalibFinish(item.key) ||
        timedOutActionKey === serviceModeActionKey.catalogReset(item.key),
    })

    if (primaryAction.kind === 'hidden') {
      return <div className={style.primaryButtonSlot} aria-hidden />
    }

    const handleClick = (e: MouseEvent) => {
      e.stopPropagation()
      onSelectKey(item.key)
      switch (primaryAction.kind) {
        case 'stop':
        case 'service':
        case 'choose-actions':
          dispatchCatalogService(item)
          break
        case 'finish':
          dispatchCalibFinish(item)
          break
        case 'calibrate':
          dispatchCalib(item, ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT)
          break
        default:
          break
      }
    }

    const labelContent =
      primaryAction.kind === 'choose-actions' ? (
        <ChooseActionsLabel />
      ) : primaryAction.kind === 'stop' ? (
        'Stop'
      ) : primaryAction.kind === 'finish' ? (
        'Finish'
      ) : primaryAction.kind === 'calibrate' ? (
        'Calibrate'
      ) : (
        primaryAction.serviceLabel
      )

    return (
      <div className={style.primaryButtonSlot}>
        <Button.Outlined
          className={clsx(
            style.catalogPrimaryButton,
            primaryAction.kind === 'choose-actions' && style.catalogPrimaryButtonMultiline
          )}
          loading={primaryAction.loading}
          disabled={primaryAction.disabled}
          title={primaryAction.title}
          onClick={handleClick}
        >
          {labelContent}
        </Button.Outlined>
      </div>
    )
  }

  const renderRow = (
    item: ServiceCalibCatalogItem,
    options?: { showStationId?: boolean }
  ) => {
    const calibrated = isServiceCalibItemCalibrated(item, calibrationRows)
    const calibratedUi =
      calibrated ||
      recentCalibFinishSuccessKey === item.key ||
      calibFinishOnboardReadyKey === item.key
    const selected = item.key === selectedKey
    const allowedActions = getCatalogItemAllowedActions(item, lotModels, robotType)
    const selectedActions = resolveSelectedCatalogActions(item, selectedActionsByKey, lotModels, robotType)
    const needsActionSelection = needsCatalogActionSelection(item, lotModels, selectedActionsByKey, robotType)
    const calibActionKey = serviceModeActionKey.catalogCalib(item.key)
    const calibFinishActionKey = serviceModeActionKey.catalogCalibFinish(item.key)
    const resetActionKey = serviceModeActionKey.catalogReset(item.key)
    const calibLoading = isActionLoading(calibActionKey)
    const calibFinishLoading = isActionLoading(calibFinishActionKey)
    const resetLoading = isActionLoading(resetActionKey)
    const serviceStartLoading = isActionLoading(serviceModeActionKey.catalogService(item.key))
    const serviceStopLoading = isActionLoading(serviceModeActionKey.catalogServiceStop(item.key))
    const serviceRowActivating = isCatalogServiceRowActivating(item, serviceStartLoading)
    const rowCommandLoading =
      calibLoading ||
      calibFinishLoading ||
      resetLoading ||
      serviceStartLoading ||
      serviceStopLoading ||
      serviceRowActivating
    const showFinishCalib =
      !calibratedUi && (calibFinishLoading || confirmedActiveCalibKey === item.key)
    const calibFailed = calibFinishFailedKeys.has(item.key)
    const blockedByOtherCatalogOperation =
      (calibSessionBlocksOtherRows && confirmedActiveCalibKey !== item.key) ||
      (commandsLockedEffective && !rowCommandLoading)
    /** Lock action chips on every row while any calib session is active (Finish shown). */
    const rowActionsLocked = calibSessionBlocksOtherRows || (commandsLockedEffective && !rowCommandLoading)
    const rowHintBase = {
      isOnline,
      ready: calibratedUi,
      modeInProgress,
      blockedByOtherOperation: blockedByOtherCatalogOperation,
    }
    const displayName =
      item.target === 'station_lot' ? item.stationName || item.name : item.name
    // Flat single-lot: name + SN. Multi-lot nested: station name only (SN stays on group header).
    const secondaryId =
      options?.showStationId && item.stationHardwareId ? item.stationHardwareId : undefined
    const nameTitle = secondaryId ? `${displayName} · ${secondaryId}` : displayName

    return (
      <div
        key={item.key}
        className={clsx(
          style.catalogRow,
          selected && style.catalogRowSelected
        )}
        onClick={() => onSelectKey(item.key)}
      >
        <div className={style.catalogIcon}>
          <ServiceCalibPointIcon
            item={item}
            primaryAction={getCatalogServiceActionLabel(item, false, selectedActions)}
          />
        </div>
        <div className={style.catalogNameBlock} title={nameTitle}>
          <div className={style.catalogName}>{displayName}</div>
          {secondaryId != null && secondaryId !== '' && (
            <div className={style.catalogStationId}>{secondaryId}</div>
          )}
        </div>
        <div className={style.catalogMetaCell}>
          <ServicePointActionChips
            allowedActions={allowedActions}
            selectedActions={selectedActions}
            onActionToggle={rowActionsLocked ? undefined : action => toggleCatalogAction(item.key, action)}
            compact
          />
        </div>
        <div className={style.calibActions}>
          {renderPrimaryActionButton(item, {
            calibrated: calibratedUi,
            showFinishCalib,
            calibFailed,
            needsActionSelection,
            calibLoading,
            calibFinishLoading,
            serviceStartLoading,
            serviceStopLoading,
            serviceRowActivating,
            blockedByOtherCatalogOperation,
          })}
          <div className={style.calibResetSlot}>
            <button
              className={serviceStyle.resetButton}
              disabled={
                resetLoading ||
                !calibratedUi ||
                !isOnline ||
                (commandsLockedEffective && !resetLoading) ||
                modeInProgress ||
                blockedByOtherCatalogOperation ||
                rowActionsLocked
              }
              onClick={e => {
                e.stopPropagation()
                onSelectKey(item.key)
                dispatchCalib(item, ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT)
              }}
              title={
                timedOutActionKey === resetActionKey
                  ? 'No confirmation'
                  : getResetCalibButtonTitle({
                      ...rowHintBase,
                      loading: resetLoading,
                    })
              }
            >
              {resetLoading ? <ButtonDotsLoader className={serviceStyle.resetButtonDots} /> : <RemoveSvg />}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return <div className={style.loadingHint}>Loading service points…</div>
  }

  if (error) {
    return <div className={style.errorHint}>{error}</div>
  }

  if (!catalog || (catalog.stationGroups.length === 0 && catalog.maintenancePoints.length === 0)) {
    return (
      <div className={style.emptyHint}>
        No station lots or maintenance points for this facility. Add them in admin or Assets.
      </div>
    )
  }

  const flatMaintenance =
    catalog.maintenancePoints.length > 0 && catalog.maintenancePoints.length <= MAINTENANCE_FLAT_MAX

  return (
    <div className={style.catalogRoot}>
      {catalog.stationGroups.map(group => {
        if (group.lots.length === 1) {
          const item = group.lots[0]
          return (
            <div key={group.stationKey} className={style.stationGroup}>
              {renderRow(item, { showStationId: true })}
            </div>
          )
        }

        const expanded = isGroupExpanded(group.stationKey)

        return (
          <div key={group.stationKey} className={style.stationGroup}>
            <button
              type="button"
              className={style.groupToggler}
              onClick={() => toggleGroup(group.stationKey)}
              aria-expanded={expanded}
            >
              <div className={style.groupTogglerIcon}>
                <ArrowSvg className={clsx(style.groupArrow, expanded && style.groupArrowExpanded)} />
              </div>
              <span className={style.stationGroupName}>{group.stationName}</span>
              <div className={style.catalogMetaCell}>
                <span className={style.stationGroupId} title={group.stationHardwareId}>
                  {group.stationHardwareId}
                </span>
              </div>
              <div className={style.calibActions}>
                <div className={style.primaryButtonSlot} aria-hidden />
                <div className={style.calibResetSlot} aria-hidden />
              </div>
            </button>
            {expanded && group.lots.map(item => renderRow(item))}
          </div>
        )
      })}

      {flatMaintenance && (
        <div className={style.stationGroup}>
          {catalog.maintenancePoints.map(item => renderRow(item))}
        </div>
      )}

      {catalog.maintenancePoints.length > MAINTENANCE_FLAT_MAX && (
        <div className={style.stationGroup}>
          <button
            type="button"
            className={style.groupToggler}
            onClick={() => toggleGroup(MAINTENANCE_GROUP_KEY)}
            aria-expanded={isGroupExpanded(MAINTENANCE_GROUP_KEY)}
          >
            <div className={style.groupTogglerIcon}>
              <ArrowSvg
                className={clsx(style.groupArrow, isGroupExpanded(MAINTENANCE_GROUP_KEY) && style.groupArrowExpanded)}
              />
            </div>
            <span className={style.stationGroupName}>Maintenance points</span>
            <div className={style.catalogMetaCell} aria-hidden />
            <div className={style.calibActions}>
              <div className={style.primaryButtonSlot} aria-hidden />
              <div className={style.calibResetSlot} aria-hidden />
            </div>
          </button>
          {isGroupExpanded(MAINTENANCE_GROUP_KEY) && catalog.maintenancePoints.map(item => renderRow(item))}
        </div>
      )}
    </div>
  )
}

export default ServiceCalibCatalog
